import '@/integrations/firebase'

import { useLocation, useNavigate } from '@tanstack/react-router'
import { useEffect, useMemo } from 'react'

import { useMe } from '@/api/hooks/useMe'
import { useFirebaseUser } from '@/hooks/useFirebaseUser'
import FullScreenSpinner from './molecules/full-screen-spinner'

const publicRoutes = [
  '/login',
  '/forgot-password',
  '/signatures',
  '/activate-account',
  '/reset-password',
  '/kiosk',
]
// Public routes a signed-in user may also stay on (a manager testing the kiosk on their own login
// must not be bounced to /workshop). The kiosk never uses the Firebase session itself.
const sharedRoutes = ['/kiosk']
const defaultAuthenticatedRoute = '/workshop'
const defaultUnauthenticatedRoute = '/login'

export const AuthGuard = ({ children }: { children: React.ReactNode }) => {
  const { isLoading: isLoadingUser, isLoggedIn } = useFirebaseUser()
  const userQuery = useMe(isLoggedIn)
  const navigate = useNavigate()
  const location = useLocation()
  const pathname = location.pathname

  const isOnPublicRoute = useMemo(
    () => publicRoutes.some(route => pathname.startsWith(route)),
    [pathname],
  )

  const isOnSharedRoute = useMemo(
    () => sharedRoutes.some(route => pathname.startsWith(route)),
    [pathname],
  )

  useEffect(() => {
    if (isLoadingUser) return

    if (isLoggedIn) {
      if (pathname === '/') {
        navigate({ replace: true, to: defaultAuthenticatedRoute })
        return
      }

      if (isOnPublicRoute && !isOnSharedRoute) {
        navigate({ replace: true, to: defaultAuthenticatedRoute })
        return
      }
    } else {
      if (!isOnPublicRoute) {
        navigate({ replace: true, to: defaultUnauthenticatedRoute })

        return
      }
    }
  }, [isLoadingUser, isLoggedIn, pathname, navigate, isOnSharedRoute])

  // The kiosk renders straight away: it must not wait on (or flash a spinner for) Firebase/me.
  if (isOnSharedRoute) return children

  if (isLoadingUser || userQuery.isLoading) {
    return <FullScreenSpinner />
  }

  if (!isLoggedIn && !isOnPublicRoute) {
    return <FullScreenSpinner />
  }

  if (isLoggedIn && isOnPublicRoute) {
    return <FullScreenSpinner />
  }

  return children
}
